import * as React from "react"
import { type Editor } from "@tiptap/react"

// --- Hooks ---
import { useTiptapEditor } from "../../tiptap-hooks/use-tiptap-editor"

// --- Icons ---
import { LinkIcon } from "../../tiptap-icons/link-icon"

// --- UI Primitives ---
import type { ButtonProps } from "../../tiptap-ui-primitive/button"
import { Button } from "../../tiptap-ui-primitive/button"

export interface LinkButtonProps extends Omit<ButtonProps, "type"> {
  /**
   * Optional editor instance. If not provided, will use editor from context
   */
  editor?: Editor | null
  /**
   * Display text for the button (optional)
   */
  text?: string
}

export function isLinkActive(editor: Editor | null): boolean {
  if (!editor) return false
  return editor.isActive("link")
}

export function canSetLink(editor: Editor | null): boolean {
  if (!editor) return false
  try {
    return editor.can().setLink({ href: "" })
  } catch {
    return false
  }
}

export function setLink(editor: Editor | null, url: string, openInNewTab: boolean = true): void {
  if (!editor) return

  if (url === "") {
    editor.chain().focus().unsetLink().run()
  } else {
    editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .setLink({
        href: url,
        target: openInNewTab ? "_blank" : null,
      })
      .run()
  }
}

export function getCurrentLinkUrl(editor: Editor | null): string {
  if (!editor) return ""
  const { href } = editor.getAttributes("link")
  return href || ""
}

export const LinkButton = React.forwardRef<HTMLButtonElement, LinkButtonProps>(
  (
    {
      editor: providedEditor,
      text,
      className = "",
      disabled,
      onClick,
      children,
      ...buttonProps
    },
    ref
  ) => {
    const editor = useTiptapEditor(providedEditor)
    const [isEditing, setIsEditing] = React.useState(false)
    const [linkUrl, setLinkUrl] = React.useState("")
    const [openInNewTab, setOpenInNewTab] = React.useState(true)
    const inputRef = React.useRef<HTMLInputElement>(null)
    const popoverRef = React.useRef<HTMLDivElement>(null)

    const isActive = isLinkActive(editor)
    const isDisabled = disabled || !editor || !canSetLink(editor)

    const handleClick = React.useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(e)

        if (!e.defaultPrevented && !isDisabled && editor) {
          // Get current link URL if editing an existing link
          const currentUrl = getCurrentLinkUrl(editor)
          setLinkUrl(currentUrl)
          setIsEditing(true)

          // Focus input after state update
          setTimeout(() => {
            inputRef.current?.focus()
          }, 100)
        }
      },
      [onClick, isDisabled, editor]
    )

    const handleSubmit = React.useCallback(
      (e: React.FormEvent) => {
        e.preventDefault()
        if (editor) {
          setLink(editor, linkUrl, openInNewTab)
          setIsEditing(false)
          setLinkUrl("")
        }
      },
      [editor, linkUrl, openInNewTab]
    )

    const handleRemove = React.useCallback(() => {
      if (editor) {
        editor.chain().focus().unsetLink().run()
        setIsEditing(false)
        setLinkUrl("")
      }
    }, [editor])

    const handleCancel = React.useCallback(() => {
      setIsEditing(false)
      setLinkUrl("")
    }, [])

    // Handle click outside to close popover
    React.useEffect(() => {
      if (!isEditing) return

      const handleClickOutside = (event: MouseEvent) => {
        if (
          popoverRef.current &&
          !popoverRef.current.contains(event.target as Node) &&
          // @ts-expect-error - ref comparison with event.target needs refactoring
          event.target !== ref
        ) {
          handleCancel()
        }
      }

      const handleEscapeKey = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
          handleCancel()
        }
      }

      document.addEventListener("mousedown", handleClickOutside)
      document.addEventListener("keydown", handleEscapeKey)

      return () => {
        document.removeEventListener("mousedown", handleClickOutside)
        document.removeEventListener("keydown", handleEscapeKey)
      }
    }, [isEditing, handleCancel, ref])

    if (!editor || !editor.isEditable) {
      return null
    }

    return (
      <div className="relative inline-block">
        <Button
          type="button"
          className={className.trim()}
          disabled={isDisabled}
          data-style="ghost"
          data-active-state={isActive ? "on" : "off"}
          data-disabled={isDisabled}
          role="button"
          tabIndex={-1}
          aria-label="link"
          aria-pressed={isActive}
          tooltip="Link"
          shortcutKeys="Ctrl-k"
          onClick={handleClick}
          {...buttonProps}
          ref={ref}
        >
          {children || (
            <>
              <LinkIcon className="tiptap-button-icon" />
              {text && <span className="tiptap-button-text">{text}</span>}
            </>
          )}
        </Button>

        {isEditing && (
          <div
            ref={popoverRef}
            className="absolute top-full left-0 mt-2 p-3 bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-md shadow-lg z-50 min-w-[300px]"
            onClick={(e) => e.stopPropagation()}
          >
            <form onSubmit={handleSubmit}>
              <div className="space-y-3">
                <div>
                  <label htmlFor="link-url" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    URL
                  </label>
                  <input
                    ref={inputRef}
                    id="link-url"
                    type="url"
                    value={linkUrl}
                    onChange={(e) => setLinkUrl(e.target.value)}
                    placeholder="https://example.com"
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-zinc-600 rounded bg-white dark:bg-zinc-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    id="open-new-tab"
                    type="checkbox"
                    checked={openInNewTab}
                    onChange={(e) => setOpenInNewTab(e.target.checked)}
                    className="rounded border-gray-300 dark:border-zinc-600"
                  />
                  <label htmlFor="open-new-tab" className="text-xs text-gray-700 dark:text-gray-300">
                    Open in new tab
                  </label>
                </div>

                <div className="flex items-center gap-2 pt-2">
                  <button
                    type="submit"
                    className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {isActive ? 'Update' : 'Add'} Link
                  </button>
                  {isActive && (
                    <button
                      type="button"
                      onClick={handleRemove}
                      className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
                    >
                      Remove
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-zinc-700 hover:bg-gray-200 dark:hover:bg-zinc-600 rounded focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}
      </div>
    )
  }
)

LinkButton.displayName = "LinkButton"

export default LinkButton
